import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useI18n } from '@/i18n/I18nContext';
import { CONTACT } from '@/constants/testIds';
import { SERVICE_SLUGS, GENERAL_INQUIRIES_SLUG } from '@/lib/services';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: '',
  email: '',
  company: '',
  service: '',
  message: '',
};

export const Contact = () => {
  const { t, lang } = useI18n();
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState({ state: 'idle', message: '' });

  const onChange = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status.state === 'submitting') return;

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ state: 'error', message: t('contact.error') });
      return;
    }

    setStatus({ state: 'submitting', message: '' });

    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        service: form.service || null,
        message: form.message.trim(),
        language: lang,
      });
      setForm(initialForm);
      setStatus({ state: 'success', message: t('contact.success') });
    } catch (err) {
      setStatus({ state: 'error', message: t('contact.error') });
    }
  };

  return (
    <section
      id="contact"
      data-testid={CONTACT.section}
      className="relative py-24 sm:py-32 bg-[#050D17]"
    >
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Left: copy + contact info */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-[#C9A24E]" />
              <span className="text-[11px] uppercase tracking-[0.32em] text-[#C9A24E]">
                {t('contact.eyebrow')}
              </span>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F2F5F8] leading-[1.08]">
              {t('contact.title')}
            </h2>
            <p className="mt-6 text-[15px] text-[#C9D2E2] leading-relaxed max-w-md">
              {t('contact.subtitle')}
            </p>

            <div className="mt-10 space-y-5">
              <a
                data-testid={CONTACT.emailLink}
                href="mailto:info@stcmarineservices.com"
                className="group flex items-start gap-4 border border-[#C9A24E]/20 hover:border-[#C9A24E]/60 p-5 transition-colors"
              >
                <div className="h-10 w-10 flex items-center justify-center bg-[#C9A24E]/10 text-[#C9A24E]">
                  <Mail size={18} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[#8F9BB3]">
                    {t('contact.emailLabel')}
                  </div>
                  <div className="mt-1 font-serif text-base text-[#F2F5F8] group-hover:text-[#C9A24E] transition-colors">
                    info@stcmarineservices.com
                  </div>
                </div>
              </a>

              <a
                data-testid={CONTACT.phoneLink}
                href="tel:+306973667473"
                className="group flex items-start gap-4 border border-[#C9A24E]/20 hover:border-[#C9A24E]/60 p-5 transition-colors"
              >
                <div className="h-10 w-10 flex items-center justify-center bg-[#C9A24E]/10 text-[#C9A24E]">
                  <Phone size={18} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[#8F9BB3]">
                    {t('contact.phoneLabel')}
                  </div>
                  <div className="mt-1 font-serif text-base text-[#F2F5F8] group-hover:text-[#C9A24E] transition-colors">
                    +30 6973667473
                  </div>
                </div>
              </a>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid={CONTACT.formRoot}
              className="bg-[#0A1A2F] border border-[#C9A24E]/20 p-8 sm:p-10 space-y-6"
              noValidate
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label={t('contact.name')} required>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={onChange('name')}
                    data-testid={CONTACT.inputName}
                    className="luxe-input"
                    autoComplete="name"
                  />
                </Field>
                <Field label={t('contact.email')} required>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={onChange('email')}
                    data-testid={CONTACT.inputEmail}
                    className="luxe-input"
                    autoComplete="email"
                  />
                </Field>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label={t('contact.company')}>
                  <input
                    type="text"
                    value={form.company}
                    onChange={onChange('company')}
                    data-testid={CONTACT.inputCompany}
                    className="luxe-input"
                    autoComplete="organization"
                  />
                </Field>
                <Field label={t('contact.service')}>
                  <Select
                    value={form.service}
                    onValueChange={(v) => setForm((p) => ({ ...p, service: v }))}
                  >
                    <SelectTrigger
                      data-testid={CONTACT.selectService}
                      className="rounded-none bg-transparent border-0 border-b border-[#C9A24E]/30 px-0 h-11 text-[#F2F5F8] hover:border-[#C9A24E] focus:ring-0 focus:border-[#C9A24E]"
                    >
                      <SelectValue placeholder={t('contact.servicePlaceholder')} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A1A2F] border border-[#C9A24E]/30 text-[#F2F5F8] rounded-none">
                      {SERVICE_SLUGS.map((slug) => (
                        <SelectItem
                          key={slug}
                          value={slug}
                          data-testid={CONTACT.selectServiceItem(slug)}
                          className="rounded-none focus:bg-[#C9A24E]/10 focus:text-[#C9A24E]"
                        >
                          {t(`services.items.${slug}.name`)}
                        </SelectItem>
                      ))}
                      <SelectItem
                        key={GENERAL_INQUIRIES_SLUG}
                        value={GENERAL_INQUIRIES_SLUG}
                        data-testid={CONTACT.selectServiceItem(GENERAL_INQUIRIES_SLUG)}
                        className="rounded-none focus:bg-[#C9A24E]/10 focus:text-[#C9A24E] border-t border-[#C9A24E]/20 mt-1"
                      >
                        {t(`services.items.${GENERAL_INQUIRIES_SLUG}.name`)}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <Field label={t('contact.message')} required>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={onChange('message')}
                  data-testid={CONTACT.inputMessage}
                  className="luxe-input resize-none"
                  placeholder={t('contact.messagePlaceholder')}
                />
              </Field>

              {status.state === 'success' && (
                <div
                  data-testid={CONTACT.successAlert}
                  className="flex items-start gap-3 border border-[#C9A24E]/40 bg-[#C9A24E]/5 p-4 text-sm text-[#F2F5F8]"
                >
                  <CheckCircle2 size={18} className="text-[#C9A24E] mt-0.5" />
                  <span>{status.message}</span>
                </div>
              )}
              {status.state === 'error' && (
                <div
                  data-testid={CONTACT.errorAlert}
                  className="flex items-start gap-3 border border-red-500/50 bg-red-500/10 p-4 text-sm text-[#F2F5F8]"
                >
                  <AlertCircle size={18} className="text-red-400 mt-0.5" />
                  <span>{status.message}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={status.state === 'submitting'}
                data-testid={CONTACT.submit}
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#C9A24E] text-[#050D17] text-[12px] uppercase tracking-[0.22em] font-semibold hover:bg-[#E6CC80] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status.state === 'submitting' && <Loader2 size={16} className="animate-spin" />}
                {status.state === 'submitting' ? t('contact.submitting') : t('contact.submit')}
              </button>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        .luxe-input {
          width: 100%;
          background: transparent;
          border: 0;
          border-bottom: 1px solid rgba(201, 162, 78, 0.3);
          padding: 10px 0 12px;
          color: #F2F5F8;
          font-size: 15px;
          outline: none;
          transition: border-color 200ms ease;
        }
        .luxe-input:focus { border-bottom-color: #C9A24E; }
        .luxe-input::placeholder { color: #6E7A92; }
      `}</style>
    </section>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-[10px] uppercase tracking-[0.28em] text-[#8F9BB3] mb-2">
      {label}
      {required && <span className="text-[#C9A24E] ml-1">*</span>}
    </span>
    {children}
  </label>
);

export default Contact;
